import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Printer, Copy, Save, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { FadeIn } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { GUEST_PACK_FIXED, GUEST_PACK_ACKNOWLEDGEMENT, GUEST_PACK_DISCLAIMER } from "@/data/rural";

const STORE_KEY = "pfn_guest_pack";

const FIELDS = [
  { key: "property_name", label: "Property name", placeholder: "e.g. Wattle Ridge Farm" },
  { key: "property_address", label: "Property address", placeholder: "Street, town, NSW, postcode" },
  { key: "host_name", label: "Host name & contact number", placeholder: "e.g. Alex — 04xx xxx xxx" },
  { key: "arrival", label: "Arrival & departure details", placeholder: "e.g. Check-in from 2pm, check-out by 10am" },
  { key: "campsite", label: "Campsite location", placeholder: "e.g. River Flat paddock, follow the white markers" },
  { key: "access", label: "Property access instructions", type: "textarea", placeholder: "Gate codes, road conditions, where to park…" },
  { key: "restricted", label: "Permitted & restricted areas", type: "textarea", placeholder: "Where guests may go, and where they must not…" },
  { key: "fire_rules", label: "Fire & campfire rules", type: "textarea", placeholder: "When fires are allowed, where, and what is never allowed…" },
  { key: "waste", label: "Waste & toilet arrangements", type: "textarea", placeholder: "Toilet location, rubbish disposal, greywater rules…" },
  { key: "utilities", label: "Water & electricity availability", placeholder: "e.g. Tank water at shed (not drinking), no power at sites" },
  { key: "pets", label: "Pet policy", placeholder: "e.g. Dogs welcome on lead, never near livestock" },
  { key: "quiet_hours", label: "Quiet hours", placeholder: "e.g. 9pm – 7am" },
  { key: "livestock", label: "Livestock & wildlife protection", type: "textarea", placeholder: "Gates to close, animals to avoid, habitat to respect…" },
  { key: "emergency", label: "Emergency contacts & evacuation instructions", type: "textarea", placeholder: "Host number, assembly point, evacuation route…" },
  { key: "environmental", label: "Property-specific environmental requirements", type: "textarea", placeholder: "Biosecurity, creek rules, weed hygiene…" },
  { key: "meeting_point", label: "Emergency meeting point", placeholder: "e.g. The machinery shed near the front gate" },
];

const EMPTY = Object.fromEntries(FIELDS.map((f) => [f.key, ""]));
const LABEL = Object.fromEntries(FIELDS.map((f) => [f.key, f.label]));

const RuralGuestPack = () => {
  const [form, setForm] = useState(() => {
    try {
      return { ...EMPTY, ...JSON.parse(localStorage.getItem(STORE_KEY)) };
    } catch {
      return EMPTY;
    }
  });
  const [ack, setAck] = useState(true);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const filled = (k) => form[k]?.trim();

  const saveTemplate = () => {
    localStorage.setItem(STORE_KEY, JSON.stringify(form));
    toast.success("Template saved on this device — edit and reuse it for every booking.");
  };

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.post("/rural/guest-pack", { ...form, include_acknowledgement: ack }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `guest-pack-${(form.property_name || "property").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Guest pack downloaded — send it with every booking confirmation.");
    } catch {
      toast.error("Could not generate the PDF. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const copyEmail = async () => {
    const lines = [
      `Subject: Your stay at ${filled("property_name") || "our property"} — what to know before you arrive`,
      "",
      `Hi,`,
      "",
      `We're looking forward to hosting you at ${filled("property_name") || "our property"}.`,
      "Your guest information pack is attached (or below) — please read it before you travel and save it to your phone, as reception can be limited.",
      "",
      `Arrival: ${filled("arrival") || "(see booking)"}`,
      `Finding us: ${filled("access") || "(see pack)"}`,
      `Fire rules: ${filled("fire_rules") || "(see pack)"}`,
      "",
      "If anything is unclear, just reply to this message.",
      "",
      filled("host_name") || "Your host",
    ];
    await navigator.clipboard.writeText(lines.join("\n"));
    toast.success("Ready-to-send guest email copied to your clipboard.");
  };

  return (
    <div data-testid="rural-guestpack-page">
      <Seo title="Guest Information Pack Generator" description="Create a personalised, professional pre-arrival information pack for your Hipcamp and rural stay guests — PDF download, print and a ready-to-send email." />
      <section className="bg-sand-light/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <Link to="/rural" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-charcoal/60 hover:text-teal transition-colors">
            <ArrowLeft className="w-4 h-4" /> Rural Land
          </Link>
          <h1 className="mt-5 font-serif text-3xl sm:text-4xl text-teal tracking-tight">Guest Information Pack Generator</h1>
          <p className="mt-3 text-charcoal/70 leading-relaxed max-w-2xl">
            Fill in your property details once — get a premium PLAN FIRST guest document you can
            download, print and send with every booking. Save your template and just update the
            details that change.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-8 items-start">
          <FadeIn>
            <div className="bg-white border border-cloud p-7 space-y-5" data-testid="guestpack-form">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="text-xs font-mono uppercase tracking-[0.12em] text-charcoal/60">{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea
                      rows={2}
                      value={form[f.key]}
                      onChange={(e) => set(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      data-testid={`guestpack-input-${f.key}`}
                      className="mt-1.5 w-full bg-ivory border border-cloud px-3 py-2.5 text-sm focus:outline-none focus:border-teal resize-y"
                    />
                  ) : (
                    <input
                      value={form[f.key]}
                      onChange={(e) => set(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      data-testid={`guestpack-input-${f.key}`}
                      className="mt-1.5 w-full bg-ivory border border-cloud px-3 py-2.5 text-sm focus:outline-none focus:border-teal"
                    />
                  )}
                </div>
              ))}
              <label className="flex items-start gap-3 pt-1">
                <input
                  type="checkbox"
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                  data-testid="guestpack-acknowledgement"
                  className="mt-1 accent-[#173E47]"
                />
                <span className="text-sm text-charcoal/75 leading-relaxed">
                  Include the optional guest acknowledgement (confirms guests received and
                  understood the rules — it is not a waiver of your legal responsibilities).
                </span>
              </label>
            </div>
          </FadeIn>

          <FadeIn delay={0.08}>
            <div className="lg:sticky lg:top-24 space-y-4">
              <div className="bg-white border border-cloud max-h-[34rem] overflow-y-auto" data-testid="guestpack-preview">
                <div className="bg-teal text-ivory px-6 py-5">
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-sand">Welcome to our property</p>
                  <p className="mt-1.5 font-serif text-xl">{filled("property_name") || "Your property name"}</p>
                  <p className="text-xs text-ivory/70 mt-1">Your Stay · Property Information &amp; Guest Guidelines</p>
                </div>
                <div className="px-6 py-5 space-y-4 text-sm">
                  {["property_address", "host_name", "arrival", "meeting_point"].map((k) => (
                    <p key={k} className="text-charcoal/80">
                      <span className="font-medium text-teal">{LABEL[k]}: </span>
                      {filled(k) || <span className="text-charcoal/40 italic">(to complete)</span>}
                    </p>
                  ))}
                  {["access", "restricted", "fire_rules", "waste", "utilities", "pets", "quiet_hours", "livestock", "emergency", "environmental", "campsite"].map((k) =>
                    filled(k) ? (
                      <div key={k}>
                        <p className="font-medium text-teal">{LABEL[k]}</p>
                        <p className="text-charcoal/75 leading-relaxed mt-0.5">{form[k]}</p>
                      </div>
                    ) : null
                  )}
                  <p className="text-xs text-charcoal/50 pt-2 border-t border-cloud">
                    Plus the standard PLAN FIRST guest guidelines: land care, fire safety, wildlife,
                    waste, waterways, pets &amp; noise, emergencies and departure checks.
                    {ack ? " Includes the guest acknowledgement page." : ""}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={download}
                  disabled={busy}
                  data-testid="guestpack-download"
                  className="inline-flex items-center justify-center gap-2 bg-teal text-ivory text-sm font-medium px-4 py-3 rounded-full hover:bg-teal-dark transition-colors disabled:opacity-60"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Download PDF
                </button>
                <button
                  onClick={() => window.print()}
                  data-testid="guestpack-print"
                  className="inline-flex items-center justify-center gap-2 border border-teal text-teal text-sm font-medium px-4 py-3 rounded-full hover:bg-teal hover:text-ivory transition-colors"
                >
                  <Printer className="w-4 h-4" /> Print
                </button>
                <button
                  onClick={copyEmail}
                  data-testid="guestpack-copy-email"
                  className="inline-flex items-center justify-center gap-2 border border-cloud text-charcoal/70 text-sm px-4 py-3 rounded-full hover:border-teal hover:text-teal transition-colors"
                >
                  <Copy className="w-4 h-4" /> Copy guest email
                </button>
                <button
                  onClick={saveTemplate}
                  data-testid="guestpack-save"
                  className="inline-flex items-center justify-center gap-2 border border-cloud text-charcoal/70 text-sm px-4 py-3 rounded-full hover:border-teal hover:text-teal transition-colors"
                >
                  <Save className="w-4 h-4" /> Save template
                </button>
              </div>
              <p className="flex items-start gap-2 text-xs text-charcoal/55 leading-relaxed">
                <Mail className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                Direct email sending switches on once a verified email integration is configured —
                meanwhile, copy the guest email and attach the PDF.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default RuralGuestPack;
